import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { QueryState } from '../components/QueryState'
import { api } from '../lib/api'
import { makeLabeler, useCommonCodes } from '../lib/codes'

/**
 * 홈 — 프로토타입의 히어로·"이렇게 연결됩니다" 구성을 승계한다.
 *
 * 프로토타입 3단계의 "점수로 계산해 가장 잘 맞는 기업을 순서대로" 문구는 쓰지 않는다.
 * 적합도 배점이 아직 확정 전이고(확정요청서 B-1) 없는 기능을 있다고 적을 수 없다.
 */
export function HomePage() {
  const cards = useQuery({
    queryKey: ['solution-cards', 'home'],
    queryFn: () => api.solutionCards({ limit: 6 }),
  })
  const problems = useQuery({
    queryKey: ['problem-cards', 'home'],
    queryFn: () => api.problemCards({ limit: 6 }),
  })
  const categories = useCommonCodes('category')
  const categoryLabel = makeLabeler(categories.data)

  return (
    <>
      <section className="hero">
        <h1>
          동네의 불편함과
          <br />
          해결할 수 있는 기업을 잇습니다
        </h1>
        <p>
          시민이 등록한 생활 문제와 지역 기업의 해결 역량을 하나의 기준으로 정리해, 누가 어떤 문제를
          풀 수 있는지 보여줍니다.
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Link className="btn" to="/problems/new">
            문제 등록하기
          </Link>
          <Link className="btn ghost" to="/solutions">
            기업카드 보기
          </Link>
        </div>

        <div className="steps">
          <div className="step">
            <b>1. 문제를 등록합니다</b>
            시민이 한 문장으로 불편을 적습니다. 대상과 동네만 함께 고르면 됩니다.
          </div>
          <div className="step">
            <b>2. 기업이 해결카드를 등록합니다</b>
            해결 가능한 분야·도움방식·서비스 지역을 시민과 <b>같은 기준</b>으로 입력합니다.
          </div>
          <div className="step">
            <b>3. 같은 기준으로 견줍니다</b>
            두 카드가 같은 축으로 정리돼 있어 무엇이 맞고 무엇이 비는지 그대로 드러납니다.
          </div>
        </div>
      </section>

      <h2 className="section-title">최근 등록된 기업카드</h2>
      <QueryState
        isPending={cards.isPending}
        error={cards.error}
        isEmpty={cards.data?.items.length === 0}
        emptyMessage="아직 공개된 기업카드가 없습니다."
      >
        <div className="grid">
          {cards.data?.items.map((c) => (
            <Link key={c.id} className="card" to={`/solutions/${c.id}`}>
              <div className="card-org">{c.org.name}</div>
              <h3>{c.title}</h3>
              <p>{c.summary}</p>
              <div className="tags">
                {c.categoryCodes.map((code) => (
                  <span key={code} className="tag">
                    {categoryLabel(code)}
                  </span>
                ))}
              </div>
            </Link>
          ))}
        </div>
      </QueryState>

      <h2 className="section-title">최근 등록된 시민문제</h2>
      <QueryState
        isPending={problems.isPending}
        error={problems.error}
        isEmpty={problems.data?.items.length === 0}
        emptyMessage="아직 공개된 시민문제가 없습니다."
      >
        <div className="grid">
          {problems.data?.items.map((p) => (
            <Link key={p.id} className="card" to={`/problems/${p.id}`}>
              <div className="card-org">{p.region.name}</div>
              <h3>{p.title}</h3>
              <div className="tags">
                {(p.categoryCodes ?? []).map((code) => (
                  <span key={code} className="tag">
                    {categoryLabel(code)}
                  </span>
                ))}
              </div>
            </Link>
          ))}
        </div>
      </QueryState>
    </>
  )
}
